/**
 * Framework-agnostic SSE plumbing for the Ask AI chat panel.
 *
 * Ported near-verbatim from `athena-kb-demo-chat/src/lib/omni-chat-client.ts` (the reference
 * client CHAT-05 is built from). No Angular imports here on purpose: this file is pure string/
 * stream handling and is unit-testable with plain Jasmine fixtures, same as the demo's own
 * `omni-chat-client.test.ts`.
 *
 * The response is `POST {askUrl}` returning `text/event-stream`. `EventSource` cannot send a POST
 * body, so the stream is consumed by hand: `fetch` + `ReadableStream`, decoded incrementally, one
 * "data: " line at a time.
 */

export type OmniChatRole = 'user' | 'assistant';

export type OmniChatTurn = { role: OmniChatRole; content: string };

export type OmniChatCitation = { slug: string; title: string; url: string };

export type OmniChatStatusPhase = 'authorizing' | 'thinking';

export type OmniChatStatusEvent = { type: 'status'; phase: OmniChatStatusPhase };
export type OmniChatAnswerEvent = {
  type: 'answer';
  markdown: string;
  citations: OmniChatCitation[];
};
export type OmniChatErrorEvent = { type: 'error'; code: string; message: string };
export type OmniChatDoneEvent = { type: 'done' };

export type OmniChatEvent = OmniChatStatusEvent | OmniChatAnswerEvent | OmniChatErrorEvent | OmniChatDoneEvent;

/**
 * The one error code that isn't really an error: a role-based refusal the backend renders as a
 * polite message. The chat UI shows it as a normal assistant bubble instead of the muted error
 * state (`AskAiChat.tsx:109-114`, `omni-chat-client.ts:31-36` in the reference client).
 */
export const ACCESS_DENIED_TOPIC_CODE = 'access_denied_topic';

export function isAccessDeniedTopic(event: OmniChatErrorEvent): boolean {
  return event.code === ACCESS_DENIED_TOPIC_CODE;
}

/**
 * Split a growing text buffer into complete ("\n"-terminated) lines.
 *
 * SSE chunks can split at any byte — a line may arrive half in one chunk and half in the next — so
 * a chunk is never assumed to end on a line boundary. Only fully-terminated lines are returned;
 * everything after the last "\n" is `rest`, to be prefixed onto the next chunk.
 */
export function splitCompleteLines(buffer: string): { lines: string[]; rest: string } {
  const parts = buffer.split('\n');
  const rest = parts.pop() ?? '';
  return { lines: parts, rest };
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * Parse one raw SSE line into a typed event.
 *
 * Only "data: " lines carry payload — blank lines (the SSE event separator), comments, and
 * anything unrecognized are not events. A line whose payload isn't valid JSON or doesn't match a
 * known event shape also yields no event rather than throwing: one malformed keepalive frame must
 * not kill the whole conversation.
 *
 * Note: `KBChatController.cs`'s mid-stream interruption frame is written as
 * `event: error\ndata: {"error":"upstream_interrupted"}\n\n` — no `type` field, on a `data:` line
 * that fails the `"type" in json` check below. That frame parses to `null` here by design; the
 * stream-ended-without-a-terminal-event fallback in `consumeSseBody` is what catches it.
 */
export function parseSseLine(line: string): OmniChatEvent | null {
  if (!line.startsWith('data:')) return null;
  const raw = line.slice(5).trim();
  if (!raw) return null;

  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!json || typeof json !== 'object' || !('type' in json)) return null;

  const record = json as Record<string, unknown>;
  switch (record['type']) {
    case 'status': {
      const phase = record['phase'];
      if (phase !== 'authorizing' && phase !== 'thinking') return null;
      return { type: 'status', phase };
    }
    case 'answer': {
      const citationsRaw = record['citations'];
      const citations: OmniChatCitation[] = Array.isArray(citationsRaw)
        ? citationsRaw
            .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
            .map((c) => ({
              slug: asString(c['slug']) ?? '',
              title: asString(c['title']) ?? '',
              url: asString(c['url']) ?? '',
            }))
        : [];
      return { type: 'answer', markdown: asString(record['markdown']) ?? '', citations };
    }
    case 'error':
      return {
        type: 'error',
        code: asString(record['code']) ?? 'unknown_error',
        message: asString(record['message']) ?? 'Something went wrong.',
      };
    case 'done':
      return { type: 'done' };
    default:
      return null;
  }
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Markdown spans that must never be auto-linked into: fenced code blocks, inline code spans, and
 * any markdown link that already exists — either backend-authored, or inserted by an earlier
 * citation in the same `autoLinkCitationTitles` pass (each citation re-scans from scratch, so its
 * own freshly-inserted `[title](url)` is already present in the string and matches this branch).
 *
 * The title's word boundaries are Unicode-aware lookarounds, not `\b`: `\b` only knows ASCII
 * word characters, so an Arabic title could never match.
 */
function citationTitlePattern(title: string): RegExp {
  return new RegExp(
    `(\`\`\`[\\s\\S]*?\`\`\`|\`[^\`\\n]*\`|\\[[^\\]\\n]*\\]\\([^()\\s]*\\))|((?<![\\p{L}\\p{N}_])${escapeRegExp(title)}(?![\\p{L}\\p{N}_]))`,
    'giu',
  );
}

/** Auto-link the first plain-text mention of one citation's title. See `autoLinkCitationTitles`. */
function autoLinkOneTitle(markdown: string, title: string, url: string): string {
  const pattern = citationTitlePattern(title);
  let linked = false;
  return markdown.replace(pattern, (whole, _protectedSpan, titleMatch) => {
    if (titleMatch === undefined || linked) return whole;
    linked = true;
    return `[${titleMatch}](${url})`;
  });
}

/**
 * Turn the first plain-text, case-insensitive mention of each citation's title into a markdown
 * link to its `url` — so an answer that names a cited article by title becomes clickable inline,
 * not just listed in the bottom Sources chips. Leaves the citations list itself untouched; this
 * only rewrites the markdown text that will be rendered above it.
 *
 * Conservative by design: at most one link per citation (the first exact-title, whole-word match),
 * never inside a code span/fence, and never re-linking text that's already inside a markdown link.
 * Citations with no title or no url are left alone.
 */
export function autoLinkCitationTitles(markdown: string, citations: OmniChatCitation[]): string {
  return citations.reduce((text, citation) => {
    const title = citation.title.trim();
    if (!title || !citation.url) return text;
    return autoLinkOneTitle(text, title, citation.url);
  }, markdown);
}

/** A portal screen the signed-in user can open: its titles (any language) and its URL path. */
export interface PortalPageLink {
  titles: string[];
  url: string;
  /** Served by every system at the same route (e.g. System Reports); link the current system's copy. */
  shared?: boolean;
}

/**
 * Comparison key for a screen name: case-, spacing- and Arabic-spelling-insensitive, so
 * "Custom  Sales Export" matches "custom sales export" and "الأصناف" matches "الاصناف".
 */
export function normalizePageTitle(value: string): string {
  return value
    .normalize('NFKC')
    .replace(/[ً-ٰٟـ]/g, '') // Arabic diacritics and tatweel
    .replace(/[أإآ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Generic words a screen name may carry or drop ("Payments" vs "Payments Report"). */
const GENERIC_PAGE_WORDS = /\b(?:reports?|screen|page)\b|تقارير|تقرير|شاشة|صفحة/g;

/** A normalized title without generic words, for the fallback match in `autoLinkPageNames`. */
function loosePageTitle(normalized: string): string {
  return normalized.replace(GENERIC_PAGE_WORDS, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Protected spans (fenced code, inline code, existing markdown links) or a bold span. The bold
 * alternatives come last so a span the answer already linked is consumed as protected first.
 */
const PAGE_NAME_PATTERN = /(```[\s\S]*?```|`[^`\n]*`|\[[^\]\n]*\]\([^()\s]*\))|\*\*([^*\n]+?)\*\*|__([^_\n]+?)__/g;

/** Separators the agent uses for menu paths inside one bold span ("Sales / Sales Reports"). */
const MENU_PATH_SEPARATOR = /(\s*(?:\/|>|→|›|»|←)\s*)/;

/**
 * Link the portal screens an answer names to those screens, so "open **Custom Sales Export**"
 * takes the merchant straight there (in a new tab, like every chat link — see
 * `createExternalLinkRenderer`).
 *
 * Only **bold** text is considered: the agent's prompt bolds screen, button and status names, so a
 * bold span equal to the title of a screen the user can open is a screen reference, while plain
 * prose ("your products") is left alone. A bold menu path ("**Sales / Sales Reports**") links each
 * segment that names a screen. Each screen is linked once, at its first mention; code spans and
 * existing links are never touched. `pages` should hold only screens the user is authorized for,
 * so a link never leads to an unauthorized page.
 */
export function autoLinkPageNames(markdown: string, pages: PortalPageLink[]): string {
  // Exact title first; then a looser key without generic words, so "Payments" finds the
  // "Payments Report" screen. When several screens share a title (System Reports exists in most
  // systems), the first in `pages` wins — callers order the current system's screens first.
  const byTitle = new Map<string, string>();
  const byLooseTitle = new Map<string, string>();
  for (const page of pages) {
    if (!page.url) continue;
    for (const title of page.titles) {
      const key = normalizePageTitle(title ?? '');
      if (key.length >= 3 && !byTitle.has(key)) byTitle.set(key, page.url);
      const loose = loosePageTitle(key);
      if (loose.length >= 3 && !byLooseTitle.has(loose)) byLooseTitle.set(loose, page.url);
    }
  }
  if (byTitle.size === 0) return markdown;

  const linkedUrls = new Set<string>();
  const linkText = (text: string): string | null => {
    const key = normalizePageTitle(text);
    // The loose fallback only for multi-word names: a lone bold word like "Sales" is usually a
    // system or menu name, and must not resolve to the "Sales Reports" screen.
    const url = byTitle.get(key) ?? (key.includes(' ') ? byLooseTitle.get(loosePageTitle(key)) : undefined);
    if (!url || linkedUrls.has(url)) return null;
    linkedUrls.add(url);
    return `[${text}](${url})`;
  };

  return markdown.replace(PAGE_NAME_PATTERN, (whole, protectedSpan, starInner, underscoreInner) => {
    if (protectedSpan !== undefined) return whole;
    const inner: string = starInner ?? underscoreInner;
    const marker = starInner !== undefined ? '**' : '__';
    if (inner.includes('](') || inner.includes('`')) return whole;

    const exact = linkText(inner.trim());
    if (exact) return `${marker}${inner.replace(inner.trim(), exact)}${marker}`;

    const parts = inner.split(MENU_PATH_SEPARATOR);
    if (parts.length === 1) return whole;
    let changed = false;
    const linkedParts = parts.map((part, i) => {
      if (i % 2 === 1 || !part.trim()) return part; // separators keep their spacing
      const linked = linkText(part.trim());
      if (!linked) return part;
      changed = true;
      return part.replace(part.trim(), linked);
    });
    return changed ? `${marker}${linkedParts.join('')}${marker}` : whole;
  });
}

/** Error code synthesized client-side when the stream closes without a `done`/`error` event. */
export const STREAM_ENDED_UNEXPECTEDLY_CODE = 'stream_ended_unexpectedly';

/**
 * Drive the line-buffered parser over a response body's byte stream.
 *
 * New vs. the reference client: tracks whether a `done` or `error` event was ever yielded. If the
 * underlying read loop terminates without either — either by closing cleanly with no terminal
 * event, or by `reader.read()` itself throwing (a genuine abrupt disconnect: TLS reset, network
 * drop, proxy killed mid-response) — a synthetic `error` event is yielded before returning, so a
 * dropped connection surfaces as a visible, translated error instead of either leaving the UI
 * stuck on the typing indicator forever, or propagating a raw/untranslated thrown error up to the
 * caller (plan risk B — the proxy's own interruption frame has no `type` field and is silently
 * dropped by `parseSseLine`, so this is not defensive paranoia, it is the only thing that catches
 * that exact case today). A deliberate `AbortError` (the caller cancelled) is the one exception:
 * that's expected cancellation, not an interruption, and is left to propagate so callers can keep
 * treating it as silent/expected exactly as before.
 */
export async function* consumeSseBody(body: ReadableStream<Uint8Array>): AsyncGenerator<OmniChatEvent, void, unknown> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let sawTerminalEvent = false;
  try {
    for (;;) {
      let done: boolean;
      let value: Uint8Array | undefined;
      try {
        ({ done, value } = await reader.read());
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          throw e;
        }
        yield {
          type: 'error',
          code: STREAM_ENDED_UNEXPECTEDLY_CODE,
          message: 'The connection was interrupted.',
        };
        return;
      }
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { lines, rest } = splitCompleteLines(buffer);
      buffer = rest;
      for (const line of lines) {
        const event = parseSseLine(line);
        if (event) {
          if (event.type === 'done' || event.type === 'error') sawTerminalEvent = true;
          yield event;
        }
      }
    }
    // Flush the decoder and treat whatever's left as a final, possibly unterminated line — a
    // server that closes the stream right after its last "data: " line without a trailing "\n"
    // should not lose that event.
    buffer += decoder.decode();
    if (buffer) {
      const event = parseSseLine(buffer);
      if (event) {
        if (event.type === 'done' || event.type === 'error') sawTerminalEvent = true;
        yield event;
      }
    }
    if (!sawTerminalEvent) {
      yield {
        type: 'error',
        code: STREAM_ENDED_UNEXPECTEDLY_CODE,
        message: 'The connection was interrupted.',
      };
    }
  } finally {
    reader.releaseLock();
  }
}
